import React, { useState, useEffect, useCallback, useMemo } from 'react';

// --- (Firebase Imports) ---
import { db, auth } from './firebase'; 
import { doc, setDoc, updateDoc, arrayUnion, arrayRemove, getDoc } from 'firebase/firestore';

// --- (Routing Imports) ---
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate, useLocation, useParams } from 'react-router-dom';
import ProtectedRoute from './auth/ProtectedRoute';
import AdminRoute from './auth/AdminRoute';

// --- (Context Import) ---
import AppContext, { useAppContext } from './context/AppContext'; 
import { useAuth } from './context/AuthContext'; 
import { useEnterprise } from './context/EnterpriseConfigContext';
import { useTelemetry } from './context/TelemetryContext';
import { useCurriculum } from './context/CurriculumContext';

// --- (Core Imports) ---
import { callApi } from './api/apiClient'; 
import { initialBudgetData } from './constants/initialStates';
import { initialProgress } from './constants/modules';
import ErrorBoundary from './ErrorBoundary';

// --- (UI/Module Imports) ---
import { Toast } from '../components/shared/Toast';
import SignOnScreen from '../features/onboarding/SignOnScreen';
import CareerTrackScreen from '../features/onboarding/CareerTrackScreen';
import Dashboard from '../features/dashboard/Dashboard';
import EmployerTalentPortal from '../features/dashboard/EmployerTalentPortal';
import MyCareerPathScreen from '../features/career-path/MyCareerPathScreen';
import BetaFeedbackModal from '../components/shared/BetaFeedbackModal';
import CryptoBadge from '../components/CryptoBadge'; 
import ZeroKnowledgeSandbox from '../features/sandbox/ZeroKnowledgeSandbox';

// --- FEATURES ---
import AIResumeGrader from '../features/resume-grader/AIResumeGrader'; 
import ProfessionalismModule from '../features/pro-module/ProfessionalismModule';
import CurriculumHub from '../features/curriculum/CurriculumHub';
import BudgetSimulator from '../features/budget-simulator/BudgetSimulator'; 
import ProfessionalPassport from '../features/career-path/ProfessionalPassport.js';
import SuperAdminDashboard from '../features/admin/SuperAdminDashboard';
import CurriculumAppendix from '../features/admin/CurriculumAppendix';
import CareerArchetypeEngine from '../features/career-path/CareerArchetypeEngine'; 
import ResilienceReactor from '../features/resilience-reactor/ResilienceReactor'; 
import { Clock } from 'lucide-react'; 

export { useAppContext };

// Route Mapper for Dynamic Modules
const DynamicModuleRoute = () => {
  const { moduleId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  return (
    <ProfessionalismModule 
      moduleId={moduleId} 
      onComplete={() => navigate('/curriculum-hub')} 
      isReviewMode={location.state?.isReviewMode} 
    />
  );
};

const GlobalLayout = ({ children }) => {
  const { authUser, userProfile } = useAuth() || {};
  const { tenantConfig } = useEnterprise() || {};
  const { vciScore, readinessTier } = useTelemetry() || {};
  const navigate = useNavigate();

  const isTrialExpired = useMemo(() => {
    if (!tenantConfig?.isTrial || !tenantConfig?.trialEndsAt) return false;
    if (userProfile?.role === 'super_admin') return false;
    if (tenantConfig.trialEndsAt.seconds) {
        const expiryDate = tenantConfig.trialEndsAt.seconds * 1000;
        return Date.now() > expiryDate;
    }
    return false;
  }, [tenantConfig, userProfile]);

  const renderExpiredOverlay = () => (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-md flex items-center justify-center p-6 text-center">
        <div className="max-w-md bg-white p-10 rounded-3xl shadow-2xl border border-slate-200">
            <Clock className="w-20 h-20 text-red-500 mx-auto mb-6 animate-pulse" />
            <h2 className="text-3xl font-black text-slate-900 uppercase mb-2 tracking-tighter">Trial Concluded</h2>
            <p className="text-slate-600 mb-8 font-medium">
                The Sandbox evaluation period for <strong>{tenantConfig?.name || "your institution"}</strong> has reached its limit. 
                Contact the JobVantage strategy team to unlock the full campus deployment and retain your behavioral analytics.
            </p>
            <button 
                onClick={() => window.location.href = "mailto:sales@JobVantage.app"}
                className="w-full py-4 bg-blue-600 hover:bg-blue-700 text-white font-black uppercase tracking-wider rounded-xl shadow-lg transition-transform active:scale-95"
            >
                Request Full Deployment
            </button>
            
            <button 
                onClick={() => auth.signOut()}
                className="mt-6 text-xs font-bold text-slate-400 hover:text-slate-600 uppercase"
            >
                Sign Out
            </button>
        </div>
    </div>
  );

  return (
    <>
      {isTrialExpired && renderExpiredOverlay()}
      
      {authUser && userProfile?.role !== 'sponsor' && (
          <div className="fixed z-50 flex items-center bg-white/90 backdrop-blur-sm rounded-full pr-4 pl-1 py-1 shadow-lg border border-slate-200 cursor-pointer hover:scale-105 transition-transform bottom-4 right-4 sm:top-4 sm:right-4 sm:bottom-auto" onClick={() => navigate('/my-career-path')}>
            <CryptoBadge userProfile={userProfile} className="w-10 h-10 mr-3" />
            <div>
              <div className="text-xs font-bold text-gray-800">{vciScore} VCI</div>
              <div className="text-[10px] text-gray-500 uppercase tracking-wider">{readinessTier}</div>
            </div>
          </div>
      )}
      
      {authUser && userProfile?.role === 'super_admin' && (
          <button 
              onClick={() => navigate('/admin')}
              className="fixed z-50 top-4 left-4 bg-red-600 text-white text-xs px-2 py-1 rounded shadow-lg hover:bg-red-700 opacity-50 hover:opacity-100 transition-opacity"
          >
              Admin Panel
          </button>
      )}

      <BetaFeedbackModal />

      {children}
    </>
  );
};

const AppContent = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const { authUser, authLoading, userProfile, setUserProfile, toastMessage, setToastMessage } = useAuth() || {};
  const { tenantConfig, hasAccess } = useEnterprise() || {};
  const { 
    vciScore, readinessTier, institutionalReadinessScore, logActivity, 
    currentDifficultyTier, setCurrentDifficultyTier, logResilienceFailure,
    getThoughtSignature 
  } = useTelemetry() || {};
  
  const { 
    step1Completed, resumeReports = [], budgetData = initialBudgetData, pro101Progress = initialProgress, 
    step2Completed, step3Completed, createNewResumeReport, updateLatestResumeReport, updateBudgetData, updatePro101Progress, fetchHistoricalReports
  } = useCurriculum() || {};

  const [graderKey, setGraderKey] = useState(Date.now());
  const budgetUnlocked = !!pro101Progress['part1'];

  const handleNavigate = useCallback((screen) => {
    const moduleList = ['pro-module', 'ai-lit', 'renter-module', 'interview', 'networking', 'tech-stack', 'eq', 'leadership', 'time-mgmt', 'negotiation', 'longevity'];
    if (moduleList.includes(screen)) navigate(`/modules/${screen}`);
    else if (screen === 'career-track') navigate('/onboarding/track');
    else navigate(`/${screen}`);
  }, [navigate]);

  const handleReview = useCallback((screen) => {
    const moduleList = ['pro-module', 'ai-lit', 'renter-module', 'interview', 'networking', 'tech-stack', 'eq', 'leadership', 'time-mgmt', 'negotiation', 'longevity'];
    if (moduleList.includes(screen)) navigate(`/modules/${screen}`, { state: { isReviewMode: true } });
    else navigate(`/${screen}`, { state: { isReviewMode: true } });
  }, [navigate]);

  const handleStartFresh = useCallback((screen) => {
    if (screen === 'resume-grader') setGraderKey(Date.now());
    navigate(`/${screen}`, { state: { startFresh: true } });
  }, [navigate]);

  const goToDashboard = useCallback(() => navigate('/dashboard'), [navigate]);

  useEffect(() => {
    if (userProfile?.subscriptionType === 'PRO' && !step1Completed) {
        console.log("Strategic Refresh: Aligning permissions with Firestore...");
    }
  }, [userProfile?.subscriptionType, step1Completed]);

  useEffect(() => {
    if (!authUser || authLoading) return;
    if (location.pathname === '/') {
        if (!userProfile?.careerTrack && userProfile?.role !== 'sponsor') navigate('/onboarding/track');
        else navigate('/dashboard');
    }
  }, [authUser, authLoading, userProfile, location.pathname, navigate]);

  const acceptMission = async (mission) => {
    if (!authUser) return;
    try {
        const userRef = doc(db, 'users', authUser.uid);
        const missionEntry = { id: mission.id || Date.now().toString(), text: mission.text, type: mission.type || 'career', createdAt: Date.now(), status: 'active' };
        await updateDoc(userRef, { activeMissions: arrayUnion(missionEntry) });
    } catch (e) { console.error("Mission Accept Failed:", e); }
  };

  const completeMission = async (mission) => {
    if (!authUser) return;
    try {
        const userRef = doc(db, 'users', authUser.uid);
        await updateDoc(userRef, { activeMissions: arrayRemove(mission) });
        setToastMessage("Mission Completed.");
    } catch (e) { console.error("Mission Complete Failed:", e); }
  };

  const toggleSavedJob = async (job) => {
    if (!authUser) return;
    const existingJobIndex = userProfile.savedJobs?.findIndex(j => j.id === job.id || j.title === job.title);
    const isSaved = existingJobIndex !== -1;
    const userRef = doc(db, 'users', authUser.uid);
    try {
        if (isSaved) {
            const jobToRemove = userProfile.savedJobs[existingJobIndex];
            await updateDoc(userRef, { savedJobs: arrayRemove(jobToRemove) });
            setToastMessage("Removed from Locker.");
        } else {
            await updateDoc(userRef, { savedJobs: arrayUnion(job) });
            setToastMessage("Saved to Locker!");
        }
    } catch (e) { console.error("Job Save Failed:", e); }
  };

  const logServiceHours = useCallback(async (activityName, hours, notes = "") => {
    if (!authUser) return;
    const newEntry = { id: `svc_${Date.now()}`, date: Date.now(), activity: activityName, hours: hours, notes: notes, status: 'verified', verifiedBy: 'System' };
    const updatedLog = [newEntry, ...(userProfile.serviceLog || [])];
    setUserProfile(prev => ({ ...prev, serviceLog: updatedLog }));
    await updateDoc(doc(db, 'users', authUser.uid), { serviceLog: updatedLog });
    setToastMessage(`Service Logged: ${hours} Hours`);
  }, [authUser, userProfile, setUserProfile, setToastMessage]);

  const adjustServiceHours = useCallback(async (entryId, newHours) => {}, []);

  const appContextValue = {
    authUser, authLoading, userProfile, setUserProfile, tenantConfig, 
    resumeReports, createNewResumeReport, updateLatestResumeReport, fetchHistoricalReports,
    pro101Progress, updatePro101Progress, budgetData, updateBudgetData,
    handleNavigate, handleReview, handleStartFresh, goToDashboard,
    callApi, logServiceHours, adjustServiceHours, toastMessage, setToastMessage,
    institutionalReadinessScore, acceptMission, completeMission, toggleSavedJob,
    hasAccess, getThoughtSignature, currentDifficultyTier, setCurrentDifficultyTier,
    logResilienceFailure, logActivity, vciScore, readinessTier
  };

  if (authLoading) {
    return <div className="min-h-screen bg-gray-100 flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  if (userProfile?.role === 'sponsor') {
    return (
        <div className="max-w-4xl mx-auto p-6 bg-white rounded-3xl shadow-xl border border-slate-200 mt-12 animate-fade-in text-left">
            <div className="border-b pb-4 mb-6">
                <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Parental Telemetry Portal</h1>
                <p className="text-xs text-slate-500 font-medium">B2C Educational ROI Verification Dashboard • Year 2026 Build</p>
            </div>
            <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200">
                <h3 className="font-bold text-slate-700 text-sm mb-2">Simulated Milestone Snapshot</h3>
                <p className="text-xs text-slate-500">Student Status: Active Validation</p>
            </div>
            <button onClick={() => auth.signOut()} className="mt-6 text-xs font-bold text-red-500 uppercase hover:underline">Terminate Session</button>
        </div>
    );
  }

  return (
    <AppContext.Provider value={appContextValue}>
      <ErrorBoundary>
        <GlobalLayout>
          <Routes>
            <Route path="/" element={<SignOnScreen />} />
            
            <Route path="/resume-grader" element={<AIResumeGrader key={graderKey} onComplete={() => navigate('/dashboard')} isReviewMode={location.state?.isReviewMode || false} />} />
            <Route path="/budget-sim" element={<BudgetSimulator onComplete={() => navigate('/dashboard')} isReviewMode={location.state?.isReviewMode || false} />} />
            <Route path="/verify/:uid" element={<ProfessionalPassport onBack={() => navigate('/dashboard')} />} />
            
            {/* PUBLIC UNPROTECTED SANDBOX ROUTE FOR TECHNICAL AUDITORS */}
            <Route path="/sandbox" element={<ZeroKnowledgeSandbox />} />

            <Route path="/onboarding/track" element={<ProtectedRoute><CareerTrackScreen onComplete={() => navigate('/dashboard')} setUserProfile={(data) => { const newProfile = { ...userProfile, ...data(userProfile) }; setUserProfile(newProfile); setDoc(doc(db, 'users', authUser.uid), newProfile, { merge: true }); }} /></ProtectedRoute>} />
            
            <Route path="/dashboard" element={<ProtectedRoute><Dashboard userProfile={userProfile} step1Completed={step1Completed} step2Completed={step2Completed} step3Completed={step3Completed} budgetUnlocked={budgetUnlocked} pro101Progress={pro101Progress} onNavigate={handleNavigate} onReview={handleReview} onStartFresh={handleStartFresh} toastMessage={toastMessage} setToastMessage={setToastMessage} irsScore={institutionalReadinessScore} /></ProtectedRoute>} />
            <Route path="/employer-portal" element={<ProtectedRoute><EmployerTalentPortal onBack={() => navigate('/dashboard')} /></ProtectedRoute>} />
            <Route path="/my-career-path" element={<ProtectedRoute><MyCareerPathScreen pro101Progress={pro101Progress} /></ProtectedRoute>} />
            <Route path="/archetype-engine" element={<ProtectedRoute><CareerArchetypeEngine onComplete={() => navigate('/dashboard')} /></ProtectedRoute>} />
            <Route path="/curriculum-hub" element={<ProtectedRoute><CurriculumHub /></ProtectedRoute>} />
            <Route path="/passport-hidden" element={<ProtectedRoute><ProfessionalPassport onBack={() => navigate('/dashboard')} /></ProtectedRoute>} />
            <Route path="/resilience-reactor" element={<ProtectedRoute><ResilienceReactor /></ProtectedRoute>} />
            <Route path="/modules/:moduleId" element={<ProtectedRoute><DynamicModuleRoute /></ProtectedRoute>} />
            <Route path="/admin" element={<AdminRoute><SuperAdminDashboard onBack={() => navigate('/dashboard')} /></AdminRoute>} />
            <Route path="/admin/appendix" element={<AdminRoute><CurriculumAppendix /></AdminRoute>} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </GlobalLayout>
      </ErrorBoundary>
      <Toast message={toastMessage} onClear={() => setToastMessage(null)} />
    </AppContext.Provider>
  );
};

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-slate-50 font-sans p-4 sm:p-8 relative">
         <div className="max-w-7xl mx-auto">
             <AppContent />
         </div>
      </div>
    </Router>
  );
}

export default App;